import { CSSProperties } from "react";
import {
  DEFAULT_TABLE_COLUMN_WIDTH,
  getTableColumnOuterWidth,
} from "@src/lib/hooks/useColumnWidths";

/**
 * Flex sizing of one data column in `horizontalScroll` mode, in outer px
 * (inner width + the cell's left padding).
 *
 * The cell renders as `flex: <grow> 0 <basis>px; min-width: <basis>px`, so it
 * never shrinks below its width (the table scrolls instead) and, when the
 * container is wider than the sum of widths, takes a share of the slack
 * proportional to `grow`, up to `maxWidth`.
 */
export type TableColumnFlexSizing = {
  /** CSS `flex-grow`. 0 = fixed at `basis`. */
  grow: number;
  /** CSS `flex-basis` and `min-width`. */
  basis: number;
  /** CSS `max-width`, when the column has a cap. */
  maxWidth?: number;
};

/**
 * Grow weights are relative (1 = a default 160px column). They are scaled up
 * for CSS so a fallback absorber can use `flex-grow: 1` and take a negligible
 * share until every other growing column has frozen at its cap. (CSS only
 * hands out the full free space when the unfrozen grow factors sum to >= 1.)
 */
const GROW_SCALE = 1000;

type SizingConfig = {
  width?: number;
  flex?: CSSProperties["flex"];
  grow?: number;
  maxWidth?: number;
};

/**
 * Grow weight implied by a column's config when no `growableColumn` is set:
 * explicit `grow`, else a numeric `flex` (so tables written for the
 * non-scroll layout keep their proportions), else proportional to the width.
 */
function defaultGrowWeight(
  config: SizingConfig | undefined,
  width: number,
): number {
  if (config?.grow != null) return Math.max(0, config.grow);
  if (typeof config?.flex === "number") return Math.max(0, config.flex);
  return width / DEFAULT_TABLE_COLUMN_WIDTH;
}

/**
 * Computes per-column flex sizing for a `horizontalScroll` table.
 *
 * - Default: every data column grows in proportion to its width (or its
 *   `grow` / numeric `flex`), capped by `maxWidth`.
 * - `growableColumn` shorthand: only that column grows; others grow only if
 *   they set `grow` explicitly.
 * - A user-resized column is fixed (grow 0) at its dragged width.
 * - If no column can take unbounded slack (all fixed or capped), the last
 *   data column that wasn't resized (else the last data column) drops its cap
 *   and absorbs the rest, so rows always reach the right edge.
 */
export function computeTableColumnSizing<K extends string>({
  columns,
  widths,
  responsiveness,
  growableColumn,
  resizedColumns,
}: {
  columns: readonly K[];
  widths: Record<K, number>;
  responsiveness?: Partial<Record<K, SizingConfig>>;
  growableColumn?: K;
  resizedColumns?: ReadonlySet<K>;
}): Record<K, TableColumnFlexSizing> {
  // With `growableColumn` set but hidden (column picker, dynamic columns),
  // nothing grows implicitly; the absorber rule below fills the row.
  const growable = growableColumn;
  const entries = columns.map((col) => {
    const config = responsiveness?.[col];
    const width = widths[col];
    let weight: number;
    if (resizedColumns?.has(col)) {
      weight = 0;
    } else if (growable != null) {
      weight =
        col === growable
          ? config?.grow != null && config.grow > 0
            ? config.grow
            : 1
          : Math.max(0, config?.grow ?? 0);
    } else {
      weight = defaultGrowWeight(config, width);
    }
    return {
      col,
      basis: getTableColumnOuterWidth(width),
      weight,
      maxWidth:
        config?.maxWidth != null
          ? getTableColumnOuterWidth(config.maxWidth)
          : undefined,
    };
  });

  const hasUncappedGrower = entries.some(
    (e) => e.weight > 0 && e.maxWidth == null,
  );
  let absorberIndex = -1;
  if (!hasUncappedGrower && entries.length > 0) {
    for (let i = entries.length - 1; i >= 0; i--) {
      if (!resizedColumns?.has(entries[i].col)) {
        absorberIndex = i;
        break;
      }
    }
    if (absorberIndex === -1) absorberIndex = entries.length - 1;
  }

  const out = {} as Record<K, TableColumnFlexSizing>;
  entries.forEach((e, i) => {
    const isAbsorber = i === absorberIndex;
    out[e.col] = {
      grow: isAbsorber
        ? Math.max(1, e.weight * GROW_SCALE)
        : e.weight * GROW_SCALE,
      basis: e.basis,
      maxWidth: isAbsorber ? undefined : e.maxWidth,
    };
  });
  return out;
}

/** Inline styles for a cell (header, body or skeleton) with this sizing. */
export function getTableColumnFlexStyle(
  sizing: TableColumnFlexSizing,
): CSSProperties {
  return {
    flexGrow: sizing.grow,
    flexShrink: 0,
    flexBasis: sizing.basis,
    minWidth: sizing.basis,
    maxWidth: sizing.maxWidth,
  };
}

/**
 * Leading gutter reserved at the start of each row when a table has no
 * checkbox (multiselect) column. Keeps the first column from hugging the
 * left edge of the table container.
 */
export const LEADING_ROW_GUTTER = 16;

/** Inner width of the "···" overflow-menu column. */
export const OVERFLOW_MENU_COLUMN_WIDTH = 34;

/**
 * Minimum width of a `horizontalScroll` table: the leading gutter or checkbox
 * column, every data column's basis (outer px), the "···" column and the
 * trailing gutter. Below it the table scrolls; above it the data columns grow.
 */
export function getHorizontalScrollTableMinWidth({
  columnBases,
  checkboxColumnWidth,
  hasOverflowMenu,
  trailingEmptyColumnWidth = 0,
}: {
  columnBases: readonly number[];
  checkboxColumnWidth?: number;
  hasOverflowMenu: boolean;
  trailingEmptyColumnWidth?: number;
}): number {
  let total =
    checkboxColumnWidth != null
      ? getTableColumnOuterWidth(checkboxColumnWidth)
      : LEADING_ROW_GUTTER;
  for (const basis of columnBases) total += basis;
  if (hasOverflowMenu) {
    total += getTableColumnOuterWidth(OVERFLOW_MENU_COLUMN_WIDTH);
  }
  return total + trailingEmptyColumnWidth;
}
